import { confirm, Checkbox, useLocalization } from '@magicdoorinc/ui';
import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { leaseSubsidiesRepository } from '~/repositories/leaseSubsidiesRepository';
import type {
  CreateLeaseSubsidyDto,
  LeaseSubsidyDto,
  LeaseSubsidyItemDto,
  SubsidizerItemDto,
  UpdateLeaseSubsidyDto,
} from '~/swagger/portal/Api';
export const [LeaseSubsidiesProvider, useLeaseSubsidies] = createMagicDoorContext('LeaseSubsidies', () => {
  const { t } = useLocalization();
  const [subsidies, setSubsidies] = createSignal<LeaseSubsidyItemDto[]>([]);
  const [subsidizers, setSubsidizers] = createSignal<SubsidizerItemDto[]>([]);
  const [loading, setLoading] = createSignal<boolean>(false);
  const [error, setError] = createSignal<Error | undefined>(undefined);

  const getLeaseSubsidies = async (leaseId: string, filter?: any) => {
    setLoading(true);
    try {
      const response = await leaseSubsidiesRepository.getLeasesSubsidies({ leaseId }, filter);
      setSubsidies(response.items || []);
      setSubsidizers(response.subsidizers || []);
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const addLeaseSubsidy = async (leaseId: string, payload: CreateLeaseSubsidyDto): Promise<LeaseSubsidyDto | undefined> => {
    try {
      const response = await leaseSubsidiesRepository.createLeaseSubsidy({ leaseId }, payload);
      getLeaseSubsidies(leaseId);
      return response;
    } catch (err: any) {
      setError(err);
      throw err;
    }
  };

  const updateLeaseSubsidy = async (leaseId: string, subsidyId: string, payload: UpdateLeaseSubsidyDto): Promise<void> => {
    try {
      await leaseSubsidiesRepository.updateLeaseSubsidy({ leaseId, subsidyId }, payload);
      getLeaseSubsidies(leaseId);
    } catch (err: any) {
      setError(err);
      throw err;
    }
  };

  const deleteLeaseSubsidy = async (leaseId: string, subsidyId: string) => {
    let deleteRelatedRecords = false;

    const confirmed = await confirm({
      title: t('Delete Lease Subsidy'),
      content: (_methods) => (
        <div class="space-y-4">
          <p>{t('Are you sure you want to delete this lease subsidy?')}</p>
          <div class="flex items-center space-x-2">
            <Checkbox
              checked={deleteRelatedRecords}
              onInput={(checked) => {
                deleteRelatedRecords = checked;
              }}
              label={t('Delete related transactions')}
              showLabel
            />
          </div>
        </div>
      ),
      doneText: t('Delete'),
      cancelText: t('Cancel'),
    });

    if (!confirmed) return;

    try {
      await leaseSubsidiesRepository.deleteLeaseSubsidy({ leaseId, subsidyId }, { deleteRelatedRecords });
      setSubsidies((prev: LeaseSubsidyItemDto[]) => prev.filter((subsidy) => subsidy.id !== subsidyId));
    } catch (err: any) {
      setError(err);
      throw err;
    }
  };

  const activateLeaseSubsidy = async (leaseId: string, subsidyId: string) => {
    try {
      await leaseSubsidiesRepository.reactivateLeaseSubsidy({ leaseId, subsidyId });
      getLeaseSubsidies(leaseId);
    } catch (err: any) {
      setError(err);
      throw err;
    }
  };

  const deactivateLeaseSubsidy = async (leaseId: string, subsidyId: string) => {
    try {
      await leaseSubsidiesRepository.deactivateLeaseSubsidy({ leaseId, subsidyId });
      getLeaseSubsidies(leaseId);
    } catch (err: any) {
      setError(err);
      throw err;
    }
  };

  return {
    subsidies,
    subsidizers,
    loading,
    error,
    getLeaseSubsidies,
    addLeaseSubsidy,
    updateLeaseSubsidy,
    deleteLeaseSubsidy,
    activateLeaseSubsidy,
    deactivateLeaseSubsidy,
  };
});
